import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, MailQuestion, Send, Loader2, Check, CalendarDays, Bell } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { EmptyState } from '../layout/EmptyState';
import { NudgeComposer } from '../events/NudgeComposer';

const groupByEvent = (items) => {
  const m = {};
  items.forEach((it) => {
    if (!m[it.event_id]) {
      m[it.event_id] = { event_id: it.event_id, event_title: it.event_title, event_date: it.event_date, people: [] };
    }
    m[it.event_id].people.push(it);
  });
  return Object.values(m);
};

const initials = (name, email) => {
  const base = (name || email || '?').trim();
  const p = base.split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || base[0]?.toUpperCase();
};

export const FollowUps = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [maybe, setMaybe] = useState([]);
  const [noReply, setNoReply] = useState([]);
  const [nudged, setNudged] = useState(() => new Set());
  const [composer, setComposer] = useState(null); // { eventId, eventTitle, recipients }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/analytics/follow-ups', { params: { scope: 'upcoming' } });
      setMaybe(data.maybe || []);
      setNoReply(data.no_reply || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const openComposer = (group, people) => {
    setComposer({
      eventId: group.event_id,
      eventTitle: group.event_title || 'your event',
      recipients: people.map((p) => ({ name: p.name, email: p.email })),
    });
  };

  const markNudged = (emails) => {
    if (!composer) return;
    setNudged((prev) => {
      const n = new Set(prev);
      emails.forEach((e) => n.add(`${composer.eventId}:${(e || '').toLowerCase()}`));
      return n;
    });
  };

  const renderGroups = (items, type) => {
    const groups = groupByEvent(items);
    if (groups.length === 0) {
      return (
        <EmptyState
          icon={type === 'maybe' ? HelpCircle : MailQuestion}
          title={type === 'maybe' ? 'No "maybe" replies' : 'Everyone has replied'}
          description={type === 'maybe'
            ? 'Guests who reply "maybe" to your upcoming events will show here so you can nudge them.'
            : 'Invited guests who haven\u2019t responded to upcoming events will appear here.'}
        />
      );
    }
    return (
      <div className="space-y-5">
        {groups.map((g) => {
          const remaining = g.people.filter((p) => !nudged.has(`${g.event_id}:${(p.email || '').toLowerCase()}`));
          return (
            <div key={g.event_id} data-testid="followup-group">
              <div className="flex items-center justify-between gap-2 mb-2">
                <button
                  onClick={() => navigate(`/app/events/${g.event_id}`)}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors min-w-0"
                >
                  <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{g.event_title || 'Untitled event'}</span>
                  <Badge variant="secondary" className="flex-shrink-0">{g.people.length}</Badge>
                </button>
                <Button
                  size="sm" variant="secondary" className="gap-1.5 h-8 flex-shrink-0"
                  disabled={remaining.length === 0}
                  onClick={() => openComposer(g, remaining)}
                  data-testid="followup-nudge-all"
                >
                  <Bell className="h-3.5 w-3.5" /> Nudge all
                </Button>
              </div>
              <div className="space-y-1.5">
                {g.people.map((p) => {
                  const k = `${g.event_id}:${(p.email || '').toLowerCase()}`;
                  const done = nudged.has(k);
                  return (
                    <div key={k} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2" data-testid="followup-row">
                      <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {initials(p.name, p.email)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{p.name || p.email}</div>
                        <div className="text-xs text-muted-foreground truncate">{p.email}</div>
                      </div>
                      {done ? (
                        <span className="text-xs text-primary flex items-center gap-1 flex-shrink-0" data-testid="followup-nudged"><Check className="h-3.5 w-3.5" /> Nudged</span>
                      ) : (
                        <Button
                          size="sm" variant="ghost" className="gap-1.5 h-8 text-primary flex-shrink-0"
                          onClick={() => openComposer(g, [p])}
                          data-testid="followup-nudge-one"
                        >
                          <Send className="h-3.5 w-3.5" /> Nudge
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]" data-testid="followups-card">
      <h3 className="font-display text-lg font-semibold mb-1 flex items-center gap-2">
        <Bell className="h-[18px] w-[18px] text-primary" /> Who to follow up with
      </h3>
      <p className="text-sm text-muted-foreground mb-4">Send a friendly reminder to guests on the fence or yet to reply (upcoming events).</p>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
      ) : (
        <Tabs defaultValue="maybe">
          <TabsList data-testid="followups-tabs">
            <TabsTrigger value="maybe" data-testid="tab-followup-maybe">Maybe ({maybe.length})</TabsTrigger>
            <TabsTrigger value="noreply" data-testid="tab-followup-noreply">No reply ({noReply.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="maybe" className="mt-4">{renderGroups(maybe, 'maybe')}</TabsContent>
          <TabsContent value="noreply" className="mt-4">{renderGroups(noReply, 'noreply')}</TabsContent>
        </Tabs>
      )}

      <NudgeComposer
        eventId={composer?.eventId}
        eventTitle={composer?.eventTitle}
        recipients={composer?.recipients || []}
        open={!!composer}
        onOpenChange={(o) => !o && setComposer(null)}
        onSent={markNudged}
      />
    </div>
  );
};
